import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from 'recharts';
import toast from 'react-hot-toast';
import { FiDownload, FiFacebook } from 'react-icons/fi';
import { useLang } from '../context/LangContext';
import { cn } from '../utils/cn';
import { logMiracleScenario } from '../lib/logMiracleScenario';
import { captureFixedSizeBlob, STAT_CARD_SHARE_WIDTH, STAT_CARD_SHARE_HEIGHT } from '../utils/memeExport';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { waitForPublicImage } from '../lib/waitForPublicImage';
import MiracleTeamPicker, {
  EMPTY_TEAM,
  getTeamDisplay,
  teamToLogFields,
} from '../components/MiracleTeamPicker';
import MiracleStatCard from '../components/MiracleStatCard';
import SafeIcon from '../common/SafeIcon';
import { getEntityFlagUrl } from '../utils/entityFlagImages';

const SHARE_TIMEOUT_MS = 45000;
const MAX_EXPORT_BYTES = 10 * 1024 * 1024;

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`)),
        ms
      );
    }),
  ]);
}

// ---------- Poisson comeback model, calibrated to real data anchors ----------
function factorial(n) {
  let f = 1;
  for (let i = 2; i <= n; i++) f *= i;
  return f;
}
function poisson(k, lambda) {
  return (Math.exp(-lambda) * Math.pow(lambda, k)) / factorial(k);
}

const BASE_RATE = 1.35 / 94;
const PUSH = 1.9;
const PARK = 0.8;

function outcomeProbs(goalDiff, minute) {
  const minsLeft = Math.max(94 - minute, 0.5);
  const lambdaTrail = BASE_RATE * PUSH * minsLeft;
  const lambdaLead = BASE_RATE * PARK * minsLeft;
  let win = 0;
  let draw = 0;
  let lose = 0;
  const MAX = 9;
  for (let a = 0; a <= MAX; a++) {
    for (let b = 0; b <= MAX; b++) {
      const p = poisson(a, lambdaTrail) * poisson(b, lambdaLead);
      const net = a - b;
      if (net > goalDiff) win += p;
      else if (net === goalDiff) draw += p;
      else lose += p;
    }
  }
  return { win, draw, lose };
}

function entityTeam(entityCode, name) {
  return {
    mode: 'entity',
    entityCode,
    name,
    emoji: null,
    flagUrl: getEntityFlagUrl(entityCode),
  };
}

const PRESETS = [
  {
    id: 'arg-egy',
    label: '🇦🇷 Argentina vs Egipto — WC 2026',
    labelEn: '🇦🇷 Argentina vs Egypt — WC 2026',
    diff: 2,
    minute: 79,
    note: "Down 0-2 at 79'. Romero, Messi, Enzo. Final: 3-2.",
    noteEs: "Perdiendo 0–2 al 79'. Romero, Messi, Enzo. Final: 3–2.",
    teams: {
      a: entityTeam('ARG', 'Argentina'),
      b: entityTeam('EGY', 'Egypt'),
    },
  },
  {
    id: 'arg-fra',
    label: '🇫🇷 Francia vs Argentina — Final 2022',
    labelEn: '🇫🇷 France vs Argentina — 2022 Final',
    diff: 2,
    minute: 79,
    note: "France down 0–2 at 79'. Mbappé ties it in 97 seconds.",
    noteEs: "Francia perdía 0–2 al 79'. Mbappé empata en 97 segundos.",
    teams: {
      a: entityTeam('FRA', 'France'),
      b: entityTeam('ARG', 'Argentina'),
    },
  },
  {
    id: 'liv-mil',
    label: '🔴 Liverpool vs Milan — Estambul 2005',
    labelEn: '🔴 Liverpool vs Milan — Istanbul 2005',
    diff: 3,
    minute: 45,
    note: 'Down 0–3 at halftime. Wins on penalties.',
    noteEs: 'Perdiendo 0–3 al entretiempo. Gana por penales.',
    teams: {
      a: { mode: 'custom', entityCode: null, name: 'Liverpool', emoji: '🔴', flagUrl: null },
      b: { mode: 'custom', entityCode: null, name: 'Milan', emoji: '⚽', flagUrl: null },
    },
  },
];

const DEFAULT_PRESET = PRESETS.find((p) => p.id === 'arg-egy');

const T = {
  es: {
    title: 'La Calculadora Milagrosa',
    subtitle: '¿Qué chance real hay de remontar un partido? Datos, no excusas.',
    leadBy: 'Ventaja del que va ganando',
    minute: 'Minuto',
    goals: 'goles',
    trailWin: 'GANA el que va perdiendo',
    drawLbl: 'EMPATE',
    leadWin: 'Gana el que va ganando',
    chartTitle: 'Chance de remontar, minuto a minuto',
    presets: 'Remontadas históricas',
    caption: 'Caption listo para publicar',
    copy: 'Copiar caption',
    copied: '¡Copiado!',
    method:
      'Modelo de simulación de goles (Poisson) calibrado con datos reales: en +4,500 partidos de la Premier League desde el 2000, un equipo que gana por 2 goles al minuto 80 pierde solo el 0.6% de las veces.',
    tagline: 'Mentalidad Ganadora, Números Milagrosos',
    chartTooltip: 'Chance de remontar',
    chartMinute: 'Minuto ',
  },
  en: {
    title: 'The Miracle Calculator',
    subtitle: "What's the real chance of a comeback? Data, not excuses.",
    leadBy: "Leading team's advantage",
    minute: 'Minute',
    goals: 'goals',
    trailWin: 'TRAILING team WINS',
    drawLbl: 'DRAW',
    leadWin: 'Leading team wins',
    chartTitle: 'Comeback chance, minute by minute',
    presets: 'Historic comebacks',
    caption: 'Ready-to-post caption',
    copy: 'Copy caption',
    copied: 'Copied!',
    method:
      'Goal-simulation model (Poisson) calibrated with real data: across 4,500+ Premier League games since 2000, a team leading by 2 goals in the 80th minute loses only 0.6% of the time.',
    tagline: 'Winning Mindset, Miraculous Numbers',
    chartTooltip: 'Comeback chance',
    chartMinute: 'Minute ',
  },
};

const PAGE_TITLES = {
  es: 'La Calculadora Milagrosa | VoteNations',
  en: 'The Miracle Calculator | VoteNations',
};

function pct(x) {
  if (x >= 0.1) return `${(x * 100).toFixed(0)}%`;
  if (x >= 0.01) return `${(x * 100).toFixed(1)}%`;
  return `${(x * 100).toFixed(2)}%`;
}

function getTrailWinLabel(t, lang, teamA) {
  const name = getTeamDisplay(teamA);
  if (name) return lang === 'es' ? `${name} GANA` : `${name} WINS`;
  return t.trailWin;
}

function getLeadByLabel(t, lang, teamB) {
  const name = getTeamDisplay(teamB);
  if (name) return lang === 'es' ? `Ventaja de ${name}` : `${name}'s advantage`;
  return t.leadBy;
}

function getLeadWinLabel(t, lang, teamB) {
  const name = getTeamDisplay(teamB);
  if (name) return lang === 'es' ? `Gana ${name}` : `${name} wins`;
  return t.leadWin;
}

function getChartTooltip(t, lang, teamA) {
  const name = getTeamDisplay(teamA);
  if (name) {
    return lang === 'es' ? `Chance de que ${name} remonte` : `${name} comeback chance`;
  }
  return t.chartTooltip;
}

function buildCaption(lang, diff, minute, probs, teamA, teamB) {
  const w = pct(probs.win);
  const trail = getTeamDisplay(teamA);
  const lead = getTeamDisplay(teamB);

  if (lang === 'es') {
    if (trail && lead) {
      return `Datos reales, no bardo. ${trail} va perdiendo por ${diff} ${diff === 1 ? 'gol' : 'goles'} ante ${lead} al minuto ${minute} — apenas ${w} de chances de remontar.\n\nLos perdedores ven ese número y buscan a quién culpar. Los ganadores ven ese número y piensan: gano o muero en la cancha.\n\nEsa es la diferencia entre mentalidad de perdedor y mentalidad de campeón. 🏆\n\n#MentalidadGanadora #VoteNations`;
    }
    if (trail) {
      return `Datos reales, no bardo. ${trail}, perdiendo por ${diff} ${diff === 1 ? 'gol' : 'goles'} al minuto ${minute}, tiene apenas ${w} de chances de ganar el partido.\n\nLos perdedores ven ese número y buscan a quién culpar. Los ganadores ven ese número y piensan: gano o muero en la cancha.\n\nEsa es la diferencia entre mentalidad de perdedor y mentalidad de campeón. 🏆\n\n#MentalidadGanadora #VoteNations`;
    }
    return `Datos reales, no bardo. Un equipo que va perdiendo por ${diff} ${diff === 1 ? 'gol' : 'goles'} al minuto ${minute} tiene apenas ${w} de chances de ganar el partido.\n\nLos perdedores ven ese número y buscan a quién culpar. Los ganadores ven ese número y piensan: gano o muero en la cancha.\n\nEsa es la diferencia entre mentalidad de perdedor y mentalidad de campeón. 🏆\n\n#MentalidadGanadora #VoteNations`;
  }

  if (trail && lead) {
    return `Real data, not excuses. ${trail} trailing by ${diff} ${diff === 1 ? 'goal' : 'goals'} against ${lead} in minute ${minute} has just a ${w} chance to pull off the comeback.\n\nLosers see that number and look for someone to blame. Winners see that number and think: I'll win or die trying.\n\nThat's the difference between a loser's mindset and a champion's. 🏆\n\n#WinningMindset #VoteNations`;
  }
  if (trail) {
    return `Real data, not excuses. ${trail}, trailing by ${diff} ${diff === 1 ? 'goal' : 'goals'} in minute ${minute}, has just a ${w} chance of winning the game.\n\nLosers see that number and look for someone to blame. Winners see that number and think: I'll win or die trying.\n\nThat's the difference between a loser's mindset and a champion's. 🏆\n\n#WinningMindset #VoteNations`;
  }
  return `Real data, not excuses. A team trailing by ${diff} ${diff === 1 ? 'goal' : 'goals'} in minute ${minute} has just a ${w} chance of winning the game.\n\nLosers see that number and look for someone to blame. Winners see that number and think: I'll win or die trying.\n\nThat's the difference between a loser's mindset and a champion's. 🏆\n\n#WinningMindset #VoteNations`;
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

export default function Chances() {
  const { lang, t: lt } = useLang();
  const [diff, setDiff] = useState(2);
  const [minute, setMinute] = useState(79);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [activePreset, setActivePreset] = useState('arg-egy');
  const [teamA, setTeamA] = useState({ ...DEFAULT_PRESET.teams.a });
  const [teamB, setTeamB] = useState({ ...DEFAULT_PRESET.teams.b });
  const skipSliderLogRef = useRef(false);
  const isFirstSliderEffectRef = useRef(true);
  const statCardRef = useRef(null);
  const shareCardRef = useRef(null);
  const t = T[lang];

  const probs = useMemo(() => outcomeProbs(diff, minute), [diff, minute]);

  const chartData = useMemo(() => {
    const arr = [];
    for (let m = 1; m <= 93; m += 1) {
      arr.push({ m, win: outcomeProbs(diff, m).win * 100 });
    }
    return arr;
  }, [diff]);

  const caption = buildCaption(lang, diff, minute, probs, teamA, teamB);
  const chartTooltipLabel = getChartTooltip(t, lang, teamA);

  useEffect(() => {
    document.title = PAGE_TITLES[lang];
    return () => {
      document.title = 'VoteNations';
    };
  }, [lang]);

  const logScenario = useCallback(
    (presetId = null) => {
      logMiracleScenario({ goalDiff: diff, minute, presetId, lang, teamA, teamB });
    },
    [diff, minute, lang, teamA, teamB]
  );

  useEffect(() => {
    if (isFirstSliderEffectRef.current) {
      isFirstSliderEffectRef.current = false;
      return;
    }
    if (skipSliderLogRef.current) {
      skipSliderLogRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      logScenario(null);
    }, 500);

    return () => clearTimeout(timer);
  }, [diff, minute, logScenario]);

  const generateStatCardBlob = async () => {
    if (!statCardRef.current) throw new Error('Stat card not ready');
    const blob = await captureFixedSizeBlob(statCardRef.current);
    if (blob.size > MAX_EXPORT_BYTES) {
      throw new Error(`Image too large (${Math.round(blob.size / 1024 / 1024)}MB)`);
    }
    return blob;
  };

  const generateShareCardBlob = async () => {
    if (!shareCardRef.current) throw new Error('Share card not ready');
    const blob = await captureFixedSizeBlob(
      shareCardRef.current,
      STAT_CARD_SHARE_WIDTH,
      STAT_CARD_SHARE_HEIGHT
    );
    if (blob.size > MAX_EXPORT_BYTES) {
      throw new Error(`Image too large (${Math.round(blob.size / 1024 / 1024)}MB)`);
    }
    return blob;
  };

  const handleDownload = async () => {
    try {
      const blob = await withTimeout(generateStatCardBlob(), SHARE_TIMEOUT_MS, 'Image export');
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `votenations-miracle-${Date.now()}.png`;
      link.href = objectUrl;
      link.click();
      URL.revokeObjectURL(objectUrl);
      toast.success(lt('meme_success'));
    } catch (err) {
      console.error('Miracle card download failed:', err?.message ?? err, err);
      toast.error(lt('meme_export_error'));
    }
  };

  const handleFacebookShare = async () => {
    if (!isSupabaseConfigured) {
      toast.error(lt('meme_share_error'));
      return;
    }

    setSharing(true);
    try {
      await withTimeout((async () => {
        const blob = await generateShareCardBlob();
        const fileName = `miracle-${crypto.randomUUID()}-${Date.now()}.png`;

        const { error: uploadError } = await supabase.storage
          .from('meme-exports')
          .upload(fileName, blob, { contentType: 'image/png', upsert: false });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('meme-exports').getPublicUrl(fileName);
        const publicImageUrl = await waitForPublicImage(urlData.publicUrl);

        const trailDisplay = getTeamDisplay(teamA);
        const leadDisplay = getTeamDisplay(teamB);
        const titleText =
          trailDisplay && leadDisplay ? `${trailDisplay} vs ${leadDisplay}` : null;

        const { data: share, error: insertError } = await supabase
          .from('miracle_calculator_shares')
          .insert({
            image_url: publicImageUrl,
            title_text: titleText,
            caption_text: caption,
            team_a_name: teamA.name,
            team_b_name: teamB.name,
            goal_diff: diff,
            minute,
            lang,
            metadata: {
              team_a: teamToLogFields(teamA),
              team_b: teamToLogFields(teamB),
            },
          })
          .select('id')
          .single();

        if (insertError) throw insertError;

        const previewUrl = `${window.location.origin}/chances-share/${share.id}`;
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(previewUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      })(), SHARE_TIMEOUT_MS, 'Facebook share');
    } catch (err) {
      console.error('Facebook share failed:', err?.message ?? err, err);
      toast.error(lt('meme_share_error'));
    } finally {
      setSharing(false);
    }
  };

  const copyCaption = async () => {
    try {
      await copyToClipboard(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      logScenario(activePreset);
    } catch {
      /* noop */
    }
  };

  const applyPreset = (p) => {
    skipSliderLogRef.current = true;
    setDiff(p.diff);
    setMinute(p.minute);
    setActivePreset(p.id);
    if (p.teams) {
      setTeamA({ ...p.teams.a });
      setTeamB({ ...p.teams.b });
    }
    logMiracleScenario({
      goalDiff: p.diff,
      minute: p.minute,
      presetId: p.id,
      lang,
      teamA: p.teams?.a ?? teamA,
      teamB: p.teams?.b ?? teamB,
    });
  };

  const activeNote = PRESETS.find((p) => p.id === activePreset);

  const pickerProps = {
    searchPlaceholder: lt('search_countries'),
    customModeLabel: lt('chances_custom'),
    quickPickLabel: lt('chances_quick_pick'),
    clearLabel: lt('chances_clear'),
    namePlaceholder: lt('chances_team_name_placeholder'),
    emojiLabel: lt('chances_emoji_label'),
    emptyHint: lt('chances_generic_teams'),
  };

  return (
    <div className="min-h-screen pb-16">
      <div className="relative py-12 sm:py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-800 via-slate-950 to-slate-950" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black uppercase italic tracking-tighter text-glow leading-tight mb-3">
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-slate-400 font-medium max-w-xl">{t.subtitle}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        {/* Presets */}
        <section className="mb-6">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-3">
            {t.presets}
          </h2>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                className={cn(
                  'rounded-lg px-3 py-2 text-xs sm:text-sm font-bold transition-colors',
                  activePreset === p.id
                    ? 'bg-lime-400 text-slate-950'
                    : 'bg-slate-900 text-slate-200 border border-slate-800 hover:border-slate-600'
                )}
              >
                {lang === 'es' ? p.label : p.labelEn}
              </button>
            ))}
          </div>
          {activeNote && (
            <p className="mt-2 text-xs sm:text-sm text-slate-400 italic">
              {lang === 'es' ? activeNote.noteEs : activeNote.note}
            </p>
          )}
        </section>

        {/* Controls */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-5 sm:p-6 mb-5">
          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2 text-sm">
                <span className="font-semibold text-slate-300">{getLeadByLabel(t, lang, teamB)}</span>
                <span className="text-lime-400 font-black">
                  {diff} {t.goals}
                </span>
              </div>
              <input
                type="range"
                className="vn-range"
                min={1}
                max={4}
                step={1}
                value={diff}
                onChange={(e) => {
                  setDiff(+e.target.value);
                  setActivePreset(null);
                }}
              />
            </div>
            <div>
              <div className="flex justify-between mb-2 text-sm">
                <span className="font-semibold text-slate-300">{t.minute}</span>
                <span className="text-lime-400 font-black">{minute}&apos;</span>
              </div>
              <input
                type="range"
                className="vn-range"
                min={1}
                max={93}
                step={1}
                value={minute}
                onChange={(e) => {
                  setMinute(+e.target.value);
                  setActivePreset(null);
                }}
              />
            </div>
          </div>
        </section>

        {/* Stat card + headline */}
        <section className="py-4">
          <p className="text-xs font-black uppercase tracking-widest text-lime-400 mb-3 text-center">
            {getTrailWinLabel(t, lang, teamA)}
          </p>
          <MiracleStatCard
            variant="display"
            teamA={teamA}
            teamB={teamB}
            diff={diff}
            minute={minute}
            probs={probs}
            lang={lang}
          />
          {/* Fixed-size export target — in viewport at full opacity, behind page UI */}
          <div className="fixed top-0 left-0 -z-10 pointer-events-none" aria-hidden="true">
            <MiracleStatCard
              ref={statCardRef}
              variant="export"
              teamA={teamA}
              teamB={teamB}
              diff={diff}
              minute={minute}
              probs={probs}
              lang={lang}
            />
          </div>
          <div className="fixed top-[1200px] left-0 -z-10 pointer-events-none" aria-hidden="true">
            <MiracleStatCard
              ref={shareCardRef}
              variant="share"
              teamA={teamA}
              teamB={teamB}
              diff={diff}
              minute={minute}
              probs={probs}
              lang={lang}
            />
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-5 text-sm">
            <div>
              <span className="text-sky-400 font-extrabold">{pct(probs.draw)}</span>{' '}
              <span className="text-slate-500">{t.drawLbl}</span>
            </div>
            <div>
              <span className="text-emerald-400 font-extrabold">{pct(probs.lose)}</span>{' '}
              <span className="text-slate-500">{getLeadWinLabel(t, lang, teamB)}</span>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-md mx-auto">
            <button
              type="button"
              onClick={handleFacebookShare}
              disabled={sharing}
              className={cn(
                'flex items-center justify-center gap-2 py-3 bg-[#1877F2] text-white font-bold rounded-lg transition-colors text-sm',
                sharing ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#166fe5]'
              )}
            >
              <SafeIcon icon={FiFacebook} /> {sharing ? lt('meme_sharing') : lt('share')}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={sharing}
              className="flex items-center justify-center gap-2 py-3 bg-white hover:bg-slate-100 text-slate-950 font-bold rounded-lg transition-colors text-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiDownload} /> {lt('download')}
            </button>
          </div>
        </section>

        {/* Chart */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-5 my-6">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-500 px-1 pb-3">
            {t.chartTitle}
          </h2>
          <div className="w-full min-w-0" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="gWin" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a3e635" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#a3e635" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="m" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis
                  stroke="#64748b"
                  fontSize={11}
                  tickLine={false}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    background: '#020617',
                    border: '1px solid #a3e635',
                    borderRadius: 8,
                    fontSize: 12.5,
                  }}
                  labelStyle={{ color: '#f8fafc' }}
                  formatter={(v) => [`${(+v).toFixed(2)}%`, chartTooltipLabel]}
                  labelFormatter={(m) => t.chartMinute + m}
                />
                <ReferenceLine x={minute} stroke="#a3e635" strokeDasharray="4 4" />
                <Area
                  type="monotone"
                  dataKey="win"
                  stroke="#a3e635"
                  strokeWidth={2.5}
                  fill="url(#gWin)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Caption */}
        <section className="bg-slate-900 border border-lime-400/30 rounded-xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-[11px] font-black uppercase tracking-widest text-lime-400">
              {t.caption}
            </h2>
            <button
              type="button"
              onClick={copyCaption}
              className="shrink-0 bg-lime-400 text-slate-950 font-extrabold text-sm rounded-lg px-4 py-2 hover:bg-lime-300 transition-colors"
            >
              {copied ? t.copied : t.copy}
            </button>
          </div>
          <div className="whitespace-pre-wrap text-sm sm:text-base leading-relaxed text-slate-200 bg-slate-950 rounded-lg p-4 border border-slate-800">
            {caption}
          </div>
        </section>

        {/* Team customization — below results */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-5 sm:p-6 mt-6">
          <h2 className="text-sm sm:text-base font-black uppercase tracking-widest text-slate-300 mb-4">
            {lt('chances_customize_heading')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <MiracleTeamPicker
              label={lt('chances_team_trailing')}
              team={teamA}
              onChange={(next) => {
                setTeamA(next);
                setActivePreset(null);
              }}
              {...pickerProps}
            />
            <MiracleTeamPicker
              label={lt('chances_team_leading')}
              team={teamB}
              onChange={(next) => {
                setTeamB(next);
                setActivePreset(null);
              }}
              {...pickerProps}
            />
          </div>
        </section>

        {/* Methodology + tagline */}
        <p className="text-xs text-slate-500 leading-relaxed mt-5">{t.method}</p>
        <p className="text-center mt-6 text-sm font-extrabold italic text-lime-400 tracking-wide">
          {t.tagline}
        </p>
      </div>
    </div>
  );
}
